import {
  AvatarDummy,
  PostOnSocialFontLoader,
  PostOnSocialUserDetails
} from "apps/console/modules/registration/configurations/socialMedia/details/components/Preview";
import TemplateContainer from "apps/console/modules/registration/configurations/socialMedia/details/components/TemplateContainer";
import Icon from "assets/icons";
import classNames from "classnames";
import React from "react";
import { useCopies } from "shared/hooks/useCopies";
import { SocialMediaPlatform } from "shared/utils/enums";
import FullPageLoaderWithPortal from "../loader";
import { Modal, ModalBody, ModalHeader } from "../modal";
import useSocialMediaShareService from "./service";
import { SocialMediaShareProps } from "./types";
import { ApiSocialPostTemplate } from "apps/console/modules/registration/configurations/socialMedia/list/types";
import Button from "../button";
import ButtonGroup from "../button/components/buttonGroup";

function SocialMediaShare(props: SocialMediaShareProps) {
  const service = useSocialMediaShareService({
    headshot: props.headshot,
    regParticipantId: props.regParticipantId || "",
    listApi: props.listApi,
    participantData: props.participantData,
    logEvent: props.logEvent
  });
  const copies = useCopies();

  const profilePictureRequired = React.useMemo(
    () => !props.headshot && service.selectedTemplate?.properties?.userImage,
    [props.headshot, service.selectedTemplate?.properties?.userImage]
  );

  const selectedTemplateUserDetails = React.useMemo(
    () => (service.selectedTemplate ? service.selectedTemplate.properties?.userDetails : null),
    [service.selectedTemplate]
  );
  const containerRef = React.useRef<HTMLDivElement>(null);

  return (
    <Modal className="socialMediaShareModal">
      <FullPageLoaderWithPortal isLoading={service.loading} />
      <ModalHeader left={copies.get("share.on.social.media")} onClose={props.onClose} />
      <ModalBody>
        <div className="socialMediaShare">
          {service.templateList && service.templateList.length > 0 && (
            <React.Fragment>
              <ButtonGroup className="socialMediaShare_toggleBtnGrp">
                {service.templateList.map((t: ApiSocialPostTemplate) => (
                  <Button
                    key={t.id}
                    isOutline
                    label={t.name}
                    className={classNames({ selected: service.selectedTemplate?.id === t.id })}
                    onClick={() => service.onTemplateChange(t)}
                  />
                ))}
              </ButtonGroup>
            </React.Fragment>
          )}
          <div className="template_wrapper">
            {service.selectedTemplate?.styleType && (
              <PostOnSocialFontLoader styleType={service.selectedTemplate.styleType} />
            )}
            {service.templateBgBase64 && (
              <TemplateContainer
                continerRef={containerRef}
                hasContainerBorder={false}
                styleType={service.selectedTemplate?.styleType}>
                <Button.CircleIcon icon={<Icon.DownloadImage />} onClick={service.handleDownload} />
                <img src={`data:image/jpeg;base64,${service.templateBgBase64}`} alt="" className="template--image" />

                {service.selectedTemplate?.properties?.userImage && (
                  <div
                    style={{
                      position: "absolute",
                      top: service.headShotPlacement.top,
                      left: service.headShotPlacement.left,
                      height: service.headShotPlacement.height,
                      width: service.headShotPlacement.width
                    }}>
                    {service.headshotBase64 ? (
                      <img
                        src={`data:image/jpeg;base64,${service.headshotBase64}`}
                        alt="Avatar"
                        style={{ borderRadius: "50%", width: "100%" }}
                      />
                    ) : (
                      <AvatarDummy />
                    )}
                  </div>
                )}

                {selectedTemplateUserDetails && (
                  <PostOnSocialUserDetails
                    hasName={selectedTemplateUserDetails.name}
                    hasJobTitle={!!selectedTemplateUserDetails.jobTitle}
                    hasCompany={!!selectedTemplateUserDetails.companyName}
                    name={`${service.participantDetails?.firstName} ${service.participantDetails?.lastName}` || ""}
                    jobTitle={service.participantDetails?.jobTitle || ""}
                    company={service.participantDetails?.organization || ""}
                    userDetailsPosition={selectedTemplateUserDetails.position}
                    textAlign={service.selectedTemplate?.textAlign}
                    primaryColor={service.selectedTemplate?.primaryColor}
                    secondaryColor={service.selectedTemplate?.secondaryColor}
                    styleType={service.selectedTemplate?.styleType}
                    primaryFontSize={service.selectedTemplate?.primaryFontSize}
                    secondaryFontSize={service.selectedTemplate?.secondaryFontSize}
                  />
                )}
              </TemplateContainer>
            )}
          </div>
          {profilePictureRequired && (
            <p className="socialMediaShare_error">
              {copies.get("template.needs.profile.picture")}{" "}
              <Button.Link
                label={copies.get("click.here")}
                onClick={() => props.handleHeadshotUpload(service.selectedTemplate?.id)}
              />
            </p>
          )}
          {service.selectedTemplate && (
            <ButtonGroup fullWidth>
              <Button
                className="socialShareButton"
                icon={<Icon.SocialX size={13} />}
                disabled={Boolean(profilePictureRequired)}
                onClick={() => service.handleUpload(SocialMediaPlatform.X, props.shareText)}
                label={copies.get("social.x")}
                isOutline
              />
              <Button
                className="socialShareButton"
                disabled={Boolean(profilePictureRequired)}
                icon={<Icon.SocialLinkedIn />}
                label={copies.get("linkedin")}
                onClick={() => service.handleUpload(SocialMediaPlatform.LINKEDIN, props.shareText)}
                isOutline
              />
            </ButtonGroup>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
}

export default SocialMediaShare;
